import * as React from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Progress } from 'antd';
import styles from '../../lexical-editor.module.css';
import { isValidTableDimension } from './constants';
import type {
  ToolbarImageController,
  ToolbarLinkController,
  ToolbarTableController,
} from './types';

interface TableEditorModalProps {
  table: ToolbarTableController;
}

export function TableEditorModal({ table }: TableEditorModalProps) {
  return (
    <Modal
      centered
      destroyOnHidden
      footer={
        <div className={styles.linkEditorActions}>
          <Button onClick={table.closeEditor}>Cancel</Button>
          <Button
            type="primary"
            disabled={
              !isValidTableDimension(table.rowsValue, 500) ||
              !isValidTableDimension(table.columnsValue, 50)
            }
            onClick={table.insert}
          >
            Insert
          </Button>
        </div>
      }
      onCancel={table.closeEditor}
      open={table.isEditorVisible}
      title="Insert Table"
    >
      <Form layout="vertical" className={styles.linkEditorFields}>
        <div className={styles.imageEditorRow}>
          <Form.Item className={styles.imageEditorField} label="Rows">
            <Input
              ref={table.inputRef}
              aria-label="Table rows"
              inputMode="numeric"
              onChange={(event) => {
                table.setRowsValue(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  table.insert();
                }
              }}
              placeholder="5"
              value={table.rowsValue}
            />
          </Form.Item>
          <Form.Item className={styles.imageEditorField} label="Columns">
            <Input
              aria-label="Table columns"
              inputMode="numeric"
              onChange={(event) => {
                table.setColumnsValue(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  table.insert();
                }
              }}
              placeholder="5"
              value={table.columnsValue}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

interface ImageEditorModalProps {
  image: ToolbarImageController;
}

export function ImageEditorModal({ image }: ImageEditorModalProps) {
  return (
    <Modal
      centered
      destroyOnHidden
      footer={
        <div className={styles.linkEditorActions}>
          <Button onClick={image.closeEditor}>Cancel</Button>
          <Button
            type="primary"
            disabled={image.urlValue.trim() === '' || image.isUploading}
            onClick={image.insert}
          >
            Insert
          </Button>
        </div>
      }
      onCancel={image.closeEditor}
      open={image.isEditorVisible}
      title="Insert Image"
    >
      <Form layout="vertical" className={styles.linkEditorFields}>
        <Form.Item className={styles.linkEditorField} label="URL">
          <div className={styles.imageUrlRow}>
            <Input
              ref={image.inputRef}
              aria-label="Image URL"
              disabled={image.isUrlLocked}
              onChange={(event) => {
                image.setUrlValue(event.target.value);
                image.setIsUrlLocked(false);
                image.setUploadError('');
                image.setUploadProgress(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  image.insert();
                }
              }}
              placeholder="Paste or type an image URL"
              value={image.urlValue}
            />
            <input
              ref={image.fileInputRef}
              accept="image/*"
              className={styles.imageFileInput}
              onChange={image.handleFileChange}
              type="file"
            />
            <Button
              aria-label="Upload image"
              icon={<UploadOutlined />}
              loading={image.isUploading}
              disabled={image.isUploading}
              onClick={() => {
                image.fileInputRef.current?.click();
              }}
              size="middle"
            />
          </div>
        </Form.Item>
        {(image.isUploading || image.uploadProgress > 0) && (
          <div className={styles.imageUploadProgress}>
            <Progress percent={image.uploadProgress} size="small" />
          </div>
        )}
        {!!image.uploadError && (
          <div className={styles.imageUploadError}>{image.uploadError}</div>
        )}
        <div className={styles.imageEditorRow}>
          <Form.Item className={styles.imageEditorField} label="Width">
            <Input
              aria-label="Image width"
              inputMode="numeric"
              onChange={(event) => {
                image.handleWidthChange(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  image.insert();
                }
              }}
              disabled={image.isUploading}
              placeholder="Auto"
              value={image.widthValue}
            />
          </Form.Item>
          <Form.Item className={styles.imageEditorField} label="Height">
            <Input
              aria-label="Image height"
              inputMode="numeric"
              onChange={(event) => {
                image.handleHeightChange(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  image.insert();
                }
              }}
              disabled={image.isUploading}
              placeholder="Auto"
              value={image.heightValue}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

interface LinkEditorModalProps {
  link: ToolbarLinkController;
}

export function LinkEditorModal({ link }: LinkEditorModalProps) {
  return (
    <Modal
      centered
      destroyOnHidden
      footer={
        <div className={styles.linkEditorActions}>
          <Button
            onClick={() => {
              link.setIsEditorVisible(false);
            }}
          >
            Cancel
          </Button>
          {!!link.url && <Button onClick={link.remove}>Remove</Button>}
          <Button
            type="primary"
            disabled={
              link.editValue.trim() === '' ||
              (!link.selectionHasText && link.textValue.trim() === '')
            }
            onClick={link.submit}
          >
            Apply
          </Button>
        </div>
      }
      onCancel={() => {
        link.setIsEditorVisible(false);
      }}
      open={link.isEditorVisible}
      title="Insert Link"
    >
      <Form layout="vertical" className={styles.linkEditorFields}>
        {!link.selectionHasText && (
          <Form.Item className={styles.linkEditorField} label="Text">
            <Input
              aria-label="Link text"
              onChange={(event) => {
                link.setTextValue(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  link.submit();
                }
              }}
              placeholder="Text to display"
              value={link.textValue}
            />
          </Form.Item>
        )}
        <Form.Item className={styles.linkEditorField} label="URL">
          <Input
            ref={link.inputRef}
            aria-label="Link URL"
            onChange={(event) => {
              link.setEditValue(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                link.submit();
              }
            }}
            placeholder="Paste or type a URL"
            value={link.editValue}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
